import React, { useEffect, useRef, useState } from "react";
import { animate, stagger } from "animejs";
import { shouldReduceMotion } from "../../utils/motion";
import "./Navigation.scss";

const navItems = ["home", "about", "skills", "projects", "contact"];
const MOBILE_MENU_ID = "mobile-navigation-menu";

const labelFromId = (id) => id.charAt(0).toUpperCase() + id.slice(1);

const Navigation = () => {
  const rootRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileTriggerRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    if (!rootRef.current || shouldReduceMotion()) return undefined;

    const intro = animate(rootRef.current.querySelectorAll(".navigation__logo, .nav-link"), {
      opacity: [0, 1],
      translateY: [-18, 0],
      delay: stagger(65),
      duration: 720,
      ease: "outExpo"
    });

    return () => intro.revert();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);

      const scrollY = window.scrollY + 140;
      const sections = navItems
        .map((id) => document.getElementById(id))
        .filter(Boolean);

      for (let index = sections.length - 1; index >= 0; index -= 1) {
        if (sections[index].offsetTop <= scrollY) {
          setActiveSection(sections[index].id);
          break;
        }
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isMenuOpen || !mobileMenuRef.current || shouldReduceMotion()) return undefined;

    const animation = animate(mobileMenuRef.current.querySelectorAll("button"), {
      opacity: [0, 1],
      translateX: [-12, 0],
      delay: stagger(45),
      duration: 420,
      ease: "outExpo"
    });

    return () => animation.revert();
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const firstMenuButton = mobileMenuRef.current?.querySelector("button");
    firstMenuButton?.focus({ preventScroll: true });

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setIsMenuOpen(false);
      mobileTriggerRef.current?.focus({ preventScroll: true });
    };

    const handlePointerDown = (event) => {
      const root = rootRef.current;
      if (!root || root.contains(event.target)) return;
      setIsMenuOpen(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("pointerdown", handlePointerDown, { passive: true });

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [isMenuOpen]);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: shouldReduceMotion() ? "auto" : "smooth"
    });
    setIsMenuOpen(false);
  };

  return (
    <nav ref={rootRef} className={`navigation ${isScrolled ? "scrolled" : ""}`} aria-label="Primary navigation">
      <div className="navigation__content">
        <button
          type="button"
          className="navigation__logo"
          onClick={() => scrollToSection("home")}
          aria-label="Go to home section"
        >
          <span className="logo-pill" aria-hidden="true" />
          <span className="logo-text">AR</span>
        </button>

        <div className="navigation__links" aria-label="Page sections">
          {navItems.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => scrollToSection(item)}
              className={`nav-link ${activeSection === item ? "active" : ""}`}
              aria-current={activeSection === item ? "location" : undefined}
            >
              {labelFromId(item)}
            </button>
          ))}
        </div>

        <button
          ref={mobileTriggerRef}
          type="button"
          className="navigation__mobile-trigger"
          onClick={() => setIsMenuOpen((value) => !value)}
          aria-label="Toggle navigation menu"
          aria-expanded={isMenuOpen}
          aria-controls={MOBILE_MENU_ID}
        >
          <span className={`hamburger ${isMenuOpen ? "open" : ""}`} aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        </button>
      </div>

      {isMenuOpen && (
        <div
          id={MOBILE_MENU_ID}
          ref={mobileMenuRef}
          className="navigation__mobile-menu"
          aria-label="Mobile navigation"
        >
          {navItems.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => scrollToSection(item)}
              aria-current={activeSection === item ? "location" : undefined}
            >
              {labelFromId(item)}
            </button>
          ))}
        </div>
      )}
    </nav>
  );
};

export default Navigation;
